import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle } from "lucide-react";
import type { ClaimStatusTemplate } from "@/api/generated/client/models/ClaimStatusTemplate";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface DeleteStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: ClaimStatusTemplate | null;
  onConfirm: () => void;
  isDeleting: boolean;
  totalActiveTemplates: number;
}

export function DeleteStatusDialog({
  open,
  onOpenChange,
  status,
  onConfirm,
  isDeleting,
  totalActiveTemplates,
}: DeleteStatusDialogProps) {
  const isLastTemplate = totalActiveTemplates <= 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Status Template</DialogTitle>
          <DialogDescription>
            {isLastTemplate ? (
              <div className="space-y-2">
                <p>Cannot delete "{status?.displayName}".</p>
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    This is your last active status template. Every company must have at least one status template. 
                    Create a new template before deleting this one.
                  </AlertDescription>
                </Alert>
              </div>
            ) : (
              <>
                Are you sure you want to delete "{status?.displayName}"? This action cannot be undone.
                {status?.isSystemDefault && (
                  <div className="mt-2 text-destructive font-medium">
                    This is a default status and may be in use by existing claims.
                  </div>
                )}
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {isLastTemplate ? (
            <Button onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={onConfirm}
                disabled={isDeleting}
              >
                {isDeleting && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Delete Status
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
